import { observable } from 'mobx';
import moment from 'moment';
import type { Moment } from '../lib/moment';

export type CancelLimitResponseArgs = {
  id?: number;
  cancel_fee_percent: number;
  cancel_fee_amount: number;
  cancel_fee_nights: number;
  cancel_fee_mode?: 'fixed' | 'percent' | 'nights';
  limit_datetime: string;
  limit_datetime_mode?: 'days_before' | 'calendar';
  limit_datetime_timezone?: string;
  no_show: boolean;
  utc_offset?: number;
};

export type CancelLimitArgs = {
  id?: number;
  cancelFeePercent: number;
  cancelFeeAmount: number;
  cancelFeeNights: number;
  cancelFeeMode?: 'fixed' | 'percent' | 'nights';
  limitDatetime: Moment;
  limitDatetimeMode?: 'daysBefore' | 'calendar';
  limitDatetimeTimezone?: string;
  noShow: boolean;
  utcOffset?: number;
};

export const convertCancelLimitResponseToArgs = (response: CancelLimitResponseArgs): CancelLimitArgs => {
  const timezone = response.limit_datetime_timezone || 'Asia/Tokyo';
  const limitDatetime = moment(response.limit_datetime).tz(timezone);

  // Determine cancel fee mode if not specified
  let cancelFeeMode: 'fixed' | 'percent' | 'nights';
  if (response.cancel_fee_mode) {
    cancelFeeMode = response.cancel_fee_mode;
  } else {
    cancelFeeMode =
      response.cancel_fee_amount > 0 ? 'fixed' : response.cancel_fee_percent > 0 ? 'percent' : 'nights';
  }

  // Determine limit datetime mode if not specified
  let limitDatetimeMode: 'daysBefore' | 'calendar';
  if (response.limit_datetime_mode) {
    limitDatetimeMode = response.limit_datetime_mode === 'days_before' ? 'daysBefore' : 'calendar';
  } else {
    // limit_datetime_mode カラムを持たないレガシーレコード向けのフォールバック判定。
    // バックエンド (app/models/cancel_limit.rb#determine_limit_datetime_mode) の同名ロジックと同期させること。
    const hour = limitDatetime.hour();
    const minute = limitDatetime.minute();
    limitDatetimeMode = hour === 23 && minute === 59 ? 'daysBefore' : 'calendar';
  }

  return {
    id: response.id,
    cancelFeePercent: response.cancel_fee_percent,
    cancelFeeAmount: response.cancel_fee_amount,
    cancelFeeNights: response.cancel_fee_nights,
    cancelFeeMode,
    limitDatetime,
    limitDatetimeMode,
    limitDatetimeTimezone: response.limit_datetime_timezone,
    noShow: response.no_show,
    utcOffset: response.utc_offset
  };
};

export default class CancelLimit {
  @observable id?: number;
  @observable cancelFeePercent: number;
  @observable cancelFeeAmount: number;
  @observable cancelFeeNights: number;
  @observable cancelFeeMode: 'fixed' | 'percent' | 'nights';
  @observable limitDatetime: Moment;
  @observable limitDatetimeMode: 'daysBefore' | 'calendar';
  @observable limitDatetimeTimezone: string;
  @observable noShow: boolean;
  @observable utcOffset?: number;

  constructor(args: CancelLimitArgs) {
    this.id = args.id;
    this.cancelFeePercent = args.cancelFeePercent ?? 0;
    this.cancelFeeAmount = args.cancelFeeAmount ?? 0;
    this.cancelFeeNights = args.cancelFeeNights ?? 0;
    this.cancelFeeMode = args.cancelFeeMode ?? (this.cancelFeeAmount > 0 ? 'fixed' : 'percent');
    this.limitDatetime = args.limitDatetime;
    this.limitDatetimeMode = args.limitDatetimeMode ?? 'daysBefore';
    this.limitDatetimeTimezone = args.limitDatetimeTimezone ?? 'Asia/Tokyo';
    this.noShow = args.noShow ?? false;
    this.utcOffset = args.utcOffset;
  }

  // チェックイン日当日 00:00 を timezone の wall-clock で初期値にする。
  // ここで .utc() などUTCモードに変換しないこと。UTCモードのmomentをカレンダーピッカーに渡すと
  // ピッカーがUTC基準で動作し、ユーザーが選んだ時刻がJST表示で+9時間ずれて保存される。
  static buildNew(checkinDate: Moment, timezone = 'Asia/Tokyo', utcOffset?: number): CancelLimit {
    const limitDatetime = moment(checkinDate).tz(timezone).startOf('day');
    return new CancelLimit({
      cancelFeePercent: 0,
      cancelFeeAmount: 0,
      cancelFeeNights: 0,
      limitDatetime,
      limitDatetimeTimezone: timezone,
      noShow: false,
      utcOffset
    });
  }

  setCancelFeePercent(value: number) {
    this.cancelFeePercent = value;
    this.cancelFeeAmount = 0;
    this.cancelFeeNights = 0;
    this.cancelFeeMode = 'percent';
  }

  setCancelFeeAmount(value: number) {
    this.cancelFeeAmount = value;
    this.cancelFeePercent = 0;
    this.cancelFeeNights = 0;
    this.cancelFeeMode = 'fixed';
  }

  setCancelFeeNights(value: number) {
    this.cancelFeeNights = value;
    this.cancelFeeAmount = 0;
    this.cancelFeePercent = 0;
    this.cancelFeeMode = 'nights';
  }

  setCancelFeeMode(mode: 'fixed' | 'percent' | 'nights') {
    this.cancelFeeMode = mode;
    if (mode === 'fixed') {
      this.cancelFeePercent = 0;
      this.cancelFeeNights = 0;
    } else if (mode === 'percent') {
      this.cancelFeeAmount = 0;
      this.cancelFeeNights = 0;
    } else {
      this.cancelFeeAmount = 0;
      this.cancelFeePercent = 0;
    }
  }

  setLimitDatetimeMode(mode: 'daysBefore' | 'calendar') {
    this.limitDatetimeMode = mode;
  }

  setNoShow(value: boolean) {
    this.noShow = value;
  }

  setLimitDatetime(date: Moment) {
    this.limitDatetime = date;
  }

  setDaysBefore(value: number, checkinDate: Moment) {
    if (value < 0) return;
    const date = checkinDate.clone().tz(this.limitDatetimeTimezone).startOf('day');
    this.limitDatetime = date.subtract(value, 'days');
  }

  daysBefore(checkinDate: Moment): number {
    return checkinDate.clone().startOf('day').diff(this.limitDatetime.clone().startOf('day'), 'days');
  }

  validationErrors(): string[] {
    const errors: string[] = [];
    if (this.cancelFeePercent < 0 || this.cancelFeePercent > 100) {
      errors.push('キャンセルポリシーのキャンセル料率は0〜100の間で指定してください。');
    }
    return errors;
  }

  get updateParams() {
    return {
      limit_datetime: this.limitDatetime.clone().tz(this.limitDatetimeTimezone).format('YYYY-MM-DD HH:mm:ss ZZ'),
      limit_datetime_timezone: this.limitDatetimeTimezone,
      limit_datetime_mode: this.limitDatetimeMode === 'daysBefore' ? 'days_before' : 'calendar',
      cancel_fee_percent: this.cancelFeePercent,
      cancel_fee_amount: this.cancelFeeAmount,
      cancel_fee_nights: this.cancelFeeNights,
      no_show: this.noShow
    };
  }
}
